/**
 * The server (`functions/src/survey/services/siteSurveyService.ts`,
 * `MAX_CARD_TEXT_LENGTH`) hard-rejects `survey_updateNoteCards` with
 * `invalid-argument` when any single card's `text` exceeds this length. There
 * was previously no client-side guard at all, so a rep who dictated or pasted
 * a long note had their entire save silently fail past this point (SALES-3) --
 * see `saveEdit` in `NoteCard.tsx`, which splits instead of rejecting.
 *
 * Mirrored here as a literal rather than imported across the client/functions
 * boundary: this package has no build-time dependency on the `functions`
 * codebase, and adding one would pull a client-only fix into the `functions`
 * deploy leg's blast radius. Keep the two values in sync by hand if the
 * server limit ever changes.
 */
export const MAX_NOTE_CARD_TEXT_LENGTH = 2000;

/**
 * Split `text` into chunks no longer than `maxLength`, preferring (in order):
 *  1. the last line break at or before the limit, so a note's paragraphs
 *     stay intact -- the server re-joins multiple cards' text with "\n\n",
 *     so splitting on our own line breaks round-trips cleanly;
 *  2. the last space at or before the limit, so words are not cut in half;
 *  3. a hard character cut at the limit, for a single unbroken run with no
 *     breakable point at all (e.g. one 3000-character word or URL) -- this
 *     is the only path that can still divide a word, and only when there is
 *     no alternative that keeps the chunk within the server's limit.
 *
 * Returns `[text]` unchanged when it already fits.
 */
export function splitNoteText(text: string, maxLength: number): string[] {
  if (text.length <= maxLength) return [text];

  const chunks: string[] = [];
  let remaining = text;

  while (remaining.length > maxLength) {
    let splitAt = remaining.lastIndexOf('\n', maxLength);
    if (splitAt <= 0) splitAt = remaining.lastIndexOf(' ', maxLength);
    if (splitAt <= 0) splitAt = maxLength;

    // `.length`/`.slice` count UTF-16 code units, not code points -- a hard
    // cut at `maxLength` can land between the two units of a surrogate pair
    // (most emoji, some CJK extension characters), which corrupts that
    // character on the wire. Back off one unit so the pair stays intact in
    // the next chunk. (Only the hard-cut path can hit this: `\n` and ` ` are
    // single BMP units and are never a surrogate half.)
    if (splitAt > 1 && isLowSurrogate(remaining.charCodeAt(splitAt))) {
      splitAt -= 1;
    }

    const chunk = remaining.slice(0, splitAt).trim();
    if (chunk) chunks.push(chunk);
    remaining = remaining.slice(splitAt).trim();
  }
  if (remaining) chunks.push(remaining);

  // Defensive invariant: never return an empty array for non-empty input.
  // An unbroken run of ONLY whitespace longer than maxLength would otherwise
  // `.trim()` away to nothing at every step above (chunk after chunk is
  // empty and skipped) -- unreachable through NoteCard.saveEdit today, which
  // trims and rejects an empty draft before ever calling this, but a future
  // caller without that guard would have its entire edit silently discarded.
  return chunks.length > 0 ? chunks : [text];
}

function isLowSurrogate(codeUnit: number): boolean {
  return codeUnit >= 0xdc00 && codeUnit <= 0xdfff;
}
